import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button, Input } from "@heroui/react";
import { DeviceType } from "@/types/common";
import { ColorPicker } from "@/shared/components/common/ColorPicker";
import { TAGS_COLORS } from "../constants";
import { TagColor } from "../types";
import { TrashSimpleIcon } from "@phosphor-icons/react";
import { ICON_SIZE } from "@/config/constants";

export interface TagFormValues {
  name: string;
  color?: TagColor | null;
}

interface TagFormContentProps {
  initialValues?: TagFormValues;
  onSubmit: (values: TagFormValues) => Promise<void>;
  onClose?: () => void;
  onDelete: () => void;
  deviceType: DeviceType;
}

export const TagFormContent = forwardRef<{ submit: () => void; reset: () => void }, TagFormContentProps>(
  ({ initialValues, onSubmit, onClose, onDelete, deviceType }, ref) => {
    const { formValues, handleChange, handleColorChange, resetForm, errors, validateForm } = useTagForm(initialValues);

    const handleSubmit = async () => {
      if (validateForm()) {
        await onSubmit(formValues);
        onClose?.();
        resetForm();
      }
    };

    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      reset: resetForm,
    }));

    const handleNameKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && formValues.name.trim()) {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === "Escape") {
        resetForm();
        onClose?.();
      }
    };

    return (
      <div className="space-y-4">
        <Input
          autoFocus={!initialValues}
          autoComplete="off"
          errorMessage={errors.name}
          isInvalid={!!errors.name}
          name="name"
          label="Tag name"
          placeholder="Enter tag name"
          size="sm"
          value={formValues.name}
          onChange={handleChange}
          onKeyDown={handleNameKeyDown}
        />

        <ColorPicker
          colors={TAGS_COLORS}
          selectedColor={formValues.color}
          onChange={handleColorChange}
          label="Select color"
        />
        {deviceType === "mobile" && (
          <div className="flex gap-2 pt-2">
            {initialValues && (
              <Button color="danger" isIconOnly variant="flat" fullWidth onPress={onDelete}>
                <TrashSimpleIcon size={ICON_SIZE.md} />
              </Button>
            )}
            <Button variant="flat" fullWidth onPress={onClose}>
              Cancel
            </Button>
            <Button color="primary" fullWidth onPress={handleSubmit}>
              {initialValues ? "Save" : "Create"}
            </Button>
          </div>
        )}
      </div>
    );
  }
);

function useTagForm(initialValues?: TagFormValues) {
  const [formValues, setFormValues] = useState<TagFormValues>({
    name: initialValues?.name || "",
    color: initialValues?.color ?? null,
  });
  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (initialValues) {
      setFormValues({
        name: initialValues.name || "",
        color: initialValues.color ?? null,
      });
    } else {
      setFormValues({
        name: "",
        color: null,
      });
    }
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleColorChange = (color: TagColor | null) => {
    setFormValues((prev) => ({ ...prev, color }));
  };

  const validateForm = () => {
    const newErrors: { name?: string } = {};

    if (!formValues.name.trim()) {
      newErrors.name = "Tag name is required.";
    } else if (formValues.name.trim().length > 50) {
      newErrors.name = "Tag name must not exceed 50 characters.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormValues({
      name: initialValues?.name || "",
      color: initialValues?.color ?? null,
    });
    setErrors({});
  };

  return {
    formValues,
    handleChange,
    handleColorChange,
    resetForm,
    errors,
    validateForm,
  };
}
